import AttachableRiderInputBMSR from './attachable-rider-input-BMSR';
import AttachableRiderInputUniformCoverage from './attachable-rider-input-uniform-coverage';
import AttachableRiderInputLevelRankingBySalaryRank from './attachable-rider-input-level-ranking-by-salary-rank';
import AttachableRiderHeaderTable from './attachable-rider-header-table';
import { PROPOSAL_RIDERS } from '../../../../../../constant/gms-constant';
import { getProposalRidersByOrder } from '../../../../../../helper/proposal/proposal-riders';
import AttachableRiderInputAmountOfLoans from './attachable-rider-input-amount-of-loans';
import Label from '../../../../../ui/label';
import { useProposalTypeData } from '../../../../../../hooks/proposal/use-proposal-type-data';

interface Props {
    showThead?: boolean;
    isBySalaryRank: boolean;
    inputLength: number;
}

export default function AttachableRiderTable({ isBySalaryRank, inputLength }: Props) {

    const {
        isSalaryRank,
        isProductGCLI,
        coverage,
        amountOfLoans,
        inputsByUniformCoverage,
        inputsBySalaryRank,
        inputsByLevelRanking,
        ridersBMSR,
        ridersAmountOfLoans,
        ridersUniformCoverage,
        ridersLevelRanking,
        ridersSalaryRank,
        riders,
        isProductGCLI: isAmountOfLoan,
        isUniformCoverage,
        isLevelRanking,
    } = useProposalTypeData();

    const isLevelRankingAndSalaryRank = isLevelRanking || isSalaryRank
    const ridersIdsOrder = [
        PROPOSAL_RIDERS.GTPDRC.productFor.GCLI,

        PROPOSAL_RIDERS.GADBR.productFor.GYRT,
        PROPOSAL_RIDERS.GADDR.productFor.GYRT,
        PROPOSAL_RIDERS.GADDR_PLUS.productFor.GYRT,
        PROPOSAL_RIDERS.GSADDR.productFor.GYRT,
        PROPOSAL_RIDERS.GTPDR.productFor.GYRT,
        PROPOSAL_RIDERS.GTIR.productFor.GYRT,
        PROPOSAL_RIDERS.GHIR.productFor.GYRT,
        PROPOSAL_RIDERS.GAMERR.productFor.GYRT,
        PROPOSAL_RIDERS.ALCR.productFor.GYRT,
        PROPOSAL_RIDERS.BMSR.productFor.GYRT,
        PROPOSAL_RIDERS.GMR.productFor.GYRT,
        PROPOSAL_RIDERS.GDR.productFor.GYRT,
        PROPOSAL_RIDERS.GMBDR.productFor.GYRT,
        PROPOSAL_RIDERS.GCIR5.productFor.GYRT,
        PROPOSAL_RIDERS.GCIR45.productFor.GYRT,

        PROPOSAL_RIDERS.GAMERR.productFor.GPA,
        PROPOSAL_RIDERS.GMR.productFor.GPA,
        PROPOSAL_RIDERS.GMBDR.productFor.GPA,
        PROPOSAL_RIDERS.GCIR5.productFor.GPA,
        PROPOSAL_RIDERS.GCIR45.productFor.GPA,

        PROPOSAL_RIDERS.GADBR.productFor.GCI,
        PROPOSAL_RIDERS.GADDR.productFor.GCI,
        PROPOSAL_RIDERS.GADDR_PLUS.productFor.GCI,
        PROPOSAL_RIDERS.GTPDR.productFor.GCI,
        PROPOSAL_RIDERS.GTIR.productFor.GCI,
        PROPOSAL_RIDERS.GHIR.productFor.GCI,
        PROPOSAL_RIDERS.GAMERR.productFor.GCI,
        PROPOSAL_RIDERS.ALCR.productFor.GCI,
        PROPOSAL_RIDERS.BMSR.productFor.GCI,
        PROPOSAL_RIDERS.GMR.productFor.GCI,
        PROPOSAL_RIDERS.GDR.productFor.GCI,
        PROPOSAL_RIDERS.GMBDR.productFor.GCI,
    ];

    const priorityRiderList = getProposalRidersByOrder(ridersIdsOrder);
    const priorityIds = priorityRiderList.map(item => item.id);
    // Sorting for table based on priorityIds
    const sortedRidersForTable = [...riders].sort((a, b) => {
        const aPriority = priorityIds.indexOf(a.id);
        const bPriority = priorityIds.indexOf(b.id);

        if (aPriority !== -1 && bPriority !== -1) {
            // both are in priority list → sort by the list order
            return aPriority - bPriority;
        } else if (aPriority !== -1) {
            // only a is in priority → a goes first
            return -1;
        } else if (bPriority !== -1) {
            // only b is in priority → b goes first
            return 1;
        } else {
            // neither in priority → sort by id ascending
            return a.id - b.id;
        }
    });

    const bmsrIds = Object.values(PROPOSAL_RIDERS.BMSR.productFor);
    
    const ridersForTable = [
        ...sortedRidersForTable.filter((rider) => !bmsrIds.some((id) => id === rider.id)),
        ...sortedRidersForTable.filter((rider) => bmsrIds.some((id) => id === rider.id)),
    ];

    // const hideIDs = [PROPOSAL_RIDERS.GHIR.id, PROPOSAL_RIDERS.BMSR.id, PROPOSAL_RIDERS.GMR_1.id, PROPOSAL_RIDERS.GDR.id]
    // const riderList = getProposalRiders(hideIDs) 
    
    // const showTheadIDs = riderList.map(item => item.id);
    // const showThead = selectedRiders.some((r) =>
    //     showTheadIDs.includes(r.id)
    // );

    return (
        <div className="w-full overflow-hidden bg-white shadow-sm">
            <div className="border-b border-[#186cb2]/20 bg-[#186cb2]/10 px-3 py-3 sm:px-4 sm:py-3.5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-2.5">
                        <div className="min-w-0">
                            <Label
                                label="Selected Riders"
                                fontWeight="font-semibold text-gray-800"
                                fontSize="text-xs"
                            />
                            <p className="mt-0.5 text-[10px] leading-4 text-gray-500 sm:text-[11px]">
                                Configure coverage amounts for each selected rider
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[650px] border-separate border-spacing-0 text-xs">
                    <AttachableRiderHeaderTable 
                        isBySalaryRank={isSalaryRank}
                        selectedCoverageTypeState={coverage}
                        selectedAmountOfLoansCheckBox={amountOfLoans}
                        inputsByUniformCoverage={inputsByUniformCoverage}
                        inputsBySalaryRank={inputsBySalaryRank}
                        inputsByLevelRanking={inputsByLevelRanking}
                        isProductGCLI={isProductGCLI}
                    />
                    <tbody className="[&_tr:nth-child(even)]:bg-gray-50/60">
                        {ridersForTable.map((rider) => {
                            if (rider.id === PROPOSAL_RIDERS.BMSR.productFor.GYRT || rider.id === PROPOSAL_RIDERS.BMSR.productFor.GCI) {
                                return (
                                    <AttachableRiderInputBMSR
                                        selectedBMSR={ridersBMSR}
                                        key={rider.id}
                                        riderLabel={rider.label}
                                        inputLength={inputLength}
                                        rider={rider}
                                    />
                                );
                            }

                            if (isAmountOfLoan) {
                                return (
                                    <AttachableRiderInputAmountOfLoans
                                        amountOfLoansRiders={ridersAmountOfLoans}
                                        isAmountOfLoan={isAmountOfLoan}
                                        key={rider.id}
                                        rider={rider}
                                        riderLabel={rider.label}
                                    />
                                );
                            }

                            if (isUniformCoverage) {
                                return (
                                    <AttachableRiderInputUniformCoverage
                                        selectUniformCoverageRidersState={ridersUniformCoverage}
                                        selectedCoverageTypeState={coverage}
                                        key={rider.id}
                                        rider={rider}
                                        riderLabel={rider.label}
                                    />
                                );
                            }

                            if (isLevelRankingAndSalaryRank) {
                                return (
                                    <AttachableRiderInputLevelRankingBySalaryRank
                                        selectLevelRankingRidersState={ridersLevelRanking}
                                        selectSalaryRankRidersState={ridersSalaryRank}
                                        isLevelRanking={isLevelRanking}
                                        isSalaryRank={isSalaryRank}
                                        key={rider.id}
                                        rider={rider}
                                        riderLabel={rider.label}
                                    />
                                );
                            }

                            return null;
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}